








































import Vue, { PropOptions } from 'vue';
import WarRoomOnboardingStep from '@/components/partials/war-room-onboarding/WarRoomOnboardingStep.vue';
import WarRoomOnboardingFooter from '@/components/partials/war-room-onboarding/WarRoomOnboardingFooter.vue';
import BravadoCheckboxV3 from '@/components/common/BravadoCheckboxV3/BravadoCheckboxV3.vue';
import { mapActions, mapState } from 'vuex';
import { RawLocation } from 'vue-router';
import { required } from 'vuelidate/lib/validators';
import { Validation, validationMixin } from 'vuelidate';
import { JOBS_DISCOVERING_TOOLTIP_NAME } from '~/components/common/AnnouncementModal/constants';
import RadioButtonGroup from '~/components/common/RadioButtonGroup/RadioButtonGroup.vue';
import WarRoomOnboardingDataQuery from '~/apollo/queries/WarRoomOnboarding.query';
import {
  JobSearchStatusKey,
  jobSearchStatusLabels,
  jobSearchStatusKeys,
  ONBOARDING_TOOLTIP_NAME,
} from '~/components/partials/war-room-onboarding/constants';
import { ONBOARDING_JOB_SEARCH_MODAL } from '~/constants/dynamicModals';
import { JOBS_ONBOARDING_PAGE } from '~/constants';
import { NuxtError } from '@nuxt/types';

interface IData {
  loadingState: string;
  onboardingData: {
    jobSearchStatuses: Array<string>;
  };
  form: {
    jobSearchStatus: string | null;
    shouldGoToJobSurveyPage: boolean;
  };
}

export default Vue.extend({
  name: 'WarRoomOnboardingJobSearch',
  components: {
    WarRoomOnboardingStep,
    WarRoomOnboardingFooter,
    BravadoCheckboxV3,
    RadioButtonGroup,
  },
  mixins: [validationMixin],
  props: {
    nextRoute: {
      type: [Object, String],
      default: '',
    } as PropOptions<RawLocation>,
    step: {
      type: Number,
      required: true,
    } as PropOptions<number>,
    stepCount: {
      type: Number,
      default: null,
      required: true,
    } as PropOptions<number>,
    hasBackButton: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    isWelcomeVisible: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
  },
  data(): IData {
    return {
      onboardingData: {
        jobSearchStatuses: [],
      },
      form: {
        jobSearchStatus: '',
        shouldGoToJobSurveyPage: false,
      },
      loadingState: 'idle',
    };
  },
  validations: {
    form: {
      jobSearchStatus: { required },
      shouldGoToJobSurveyPage: { required },
    },
  },
  apollo: {
    onboardingData: {
      query: WarRoomOnboardingDataQuery,
    },
  },
  async fetch() {
    try {
      if (!this.onboardingProfile.username) {
        await this.fetchOnboardingProfile();
      }

      const { warRoomJobSearchStatus, warRoomIsInterestingToJobSurvey } = this.onboardingProfile;

      this.form.jobSearchStatus = warRoomJobSearchStatus;
      this.form.shouldGoToJobSurveyPage = warRoomIsInterestingToJobSurvey;
    } catch (e) {
      this.$nuxt.error(e as NuxtError);
    }
  },
  computed: {
    ...mapState('warRoom', ['onboardingProfile']),
    jobSearchStatuses(): Array<Record<string, string | boolean>> {
      return this.onboardingData.jobSearchStatuses.map((key: string) => {
        return {
          label: (jobSearchStatusLabels as Record<string, string>)[key],
          value: key,
          checked: key === this.form.jobSearchStatus,
        };
      });
    },
    isNextButtonDisabled(): boolean {
      return this.$v.form.$invalid || this.loadingState !== 'idle';
    },
  },
  methods: {
    ...mapActions('warRoom', ['fetchOnboardingProfile', 'finishOnboarding']),
    handleBack(): void {
      this.$router.go(-1);
    },
    async handleNext(): Promise<void> {
      this.loadingState = 'loading';

      try {
        await this.$api.$put('/api/war_room/user.json', {
          user: {
            war_room_job_search_status: this.form.jobSearchStatus,
            war_room_is_interesting_to_job_survey: this.form.shouldGoToJobSurveyPage,
          },
        });

        if (this.step === this.stepCount && !this.form.shouldGoToJobSurveyPage) {
          await Promise.all([this.finishOnboarding(), this.$tooltip.close(ONBOARDING_TOOLTIP_NAME)]);
          await this.$auth.fetchUser();
        }

        if (this.form.shouldGoToJobSurveyPage) {
          await this.$tooltip.close(ONBOARDING_TOOLTIP_NAME);
          await this.$tooltip.close(JOBS_DISCOVERING_TOOLTIP_NAME);

          this.$modal.hide(ONBOARDING_JOB_SEARCH_MODAL);

          await this.$auth.fetchUser();
          await this.$router.replace(JOBS_ONBOARDING_PAGE);
        } else {
          await Promise.all([this.fetchOnboardingProfile(), this.$router.push(this.nextRoute)]);
        }
      } catch (e) {
        this.$notice.error('Something went wrong', e);
      } finally {
        this.loadingState = 'idle';
      }
    },
    handleJobStatusChange($event: JobSearchStatusKey): void {
      this.form.jobSearchStatus = $event;

      if (!(this.$v.form.shouldGoToJobSurveyPage as Validation).$dirty) {
        this.form.shouldGoToJobSurveyPage = [
          jobSearchStatusKeys.ACTIVELY_LOOKING,
          jobSearchStatusKeys.PASSIVELY_LOOKING,
        ].includes($event);
      }
    },
  },
});
